import React from "react";
import { createPortal } from "react-dom";

import ChatErrorBanner from "./ChatErrorBanner";
import ChatHeader from "./ChatHeader";
import ChatInput from "./ChatInput";
import ChatMessages from "./ChatMessages";
import { CompetencyEditPanel } from "./CompetencyEditPanel";
import { FormBuilderSuggestedActions } from "./FormBuilderSuggestedActions";
import { GoalEditPanel } from "./GoalEditPanel";

import { useAskStream } from "../hooks/useAskStream";
import { useGoalCreation } from "../hooks/useGoalCreation";

import { useAskPerformYardStore } from "../AskPerformYardStore";

const ChatContent: React.FC = () => {
	const hasNoDocuments = useAskPerformYardStore((state) => state.hasNoDocuments);
	const askHrEnabled = useAskPerformYardStore((state) => state.cachedAskHrEnabled);
	const goalCreationEnabled = useAskPerformYardStore((state) => state.cachedGoalCreationEnabled);
	const formCreationEnabled = useAskPerformYardStore((state) => state.cachedFormCreationEnabled);
	const isOnFormBuilderPage = useAskPerformYardStore((state) => state.isOnFormBuilderPage);
	const conversationStarted = useAskPerformYardStore((state) => state.messages.some((m) => m.role === "user"));

	// Owned here (not in FormBuilderSuggestedActions) so the unmount-cleanup
	// doesn't abort the fetch the click just kicked off.
	const { sendQuery } = useAskStream();

	const showNoDocumentsError = askHrEnabled && hasNoDocuments && !goalCreationEnabled && !formCreationEnabled;
	const showFormBuilderChip =
		isOnFormBuilderPage && formCreationEnabled && !conversationStarted && !showNoDocumentsError;

	return (
		<>
			<ChatHeader />
			<div className="flex flex-1 flex-col overflow-hidden">
				{showNoDocumentsError ? <ChatErrorBanner type="no-documents" /> : <ChatMessages />}
			</div>
			{showFormBuilderChip && (
				<div className="px-4">
					<FormBuilderSuggestedActions onSelect={(query) => void sendQuery({ query, agent: "form" })} />
				</div>
			)}
			<ChatInput />
		</>
	);
};

export const ChatContainer: React.FC = () => {
	const isOpen = useAskPerformYardStore((state) => state.isOpen);
	const isExpanded = useAskPerformYardStore((state) => state.isExpanded);
	const close = useAskPerformYardStore((state) => state.close);
	const isGoalEditPanelOpen = useAskPerformYardStore((state) => state.isGoalEditPanelOpen);
	const closeGoalEditPanel = useAskPerformYardStore((state) => state.closeGoalEditPanel);
	const isCompetencyEditPanelOpen = useAskPerformYardStore((state) => state.isCompetencyEditPanelOpen);
	const closeCompetencyEditPanel = useAskPerformYardStore((state) => state.closeCompetencyEditPanel);
	const markGoalDeleted = useAskPerformYardStore((state) => state.markGoalDeleted);
	const creatingSuggestionIds = useAskPerformYardStore((state) => state.creatingSuggestionIds);
	const isCreatingAll = useAskPerformYardStore((state) => state.isCreatingAll);
	const { handleCreateGoal, handleCreateAllGoals } = useGoalCreation();

	// Lock body scroll when fullscreen
	React.useEffect(() => {
		if (!(isOpen && isExpanded)) return;
		const originalBodyOverflow = document.body.style.overflow;
		const originalHtmlOverflow = document.documentElement.style.overflow;
		document.body.style.overflow = "hidden";
		document.documentElement.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = originalBodyOverflow;
			document.documentElement.style.overflow = originalHtmlOverflow;
		};
	}, [isOpen, isExpanded]);

	if (!isOpen) {
		return null;
	}

	return createPortal(
		<div className={isExpanded ? "fixed inset-0 z-[1300] flex justify-end" : "fixed bottom-24 right-6 z-[1200]"}>
			{/* Backdrop — fullscreen only */}
			{isExpanded && (
				<button
					type="button"
					className="absolute inset-0 border-none bg-black/30 duration-200 animate-in fade-in"
					onClick={close}
					aria-label="Close chat"
				/>
			)}

			{/* Chat panel — key ensures React preserves identity when siblings change */}
			<div
				key="chat-panel"
				className={
					isExpanded
						? "relative flex h-full w-[416px] flex-col bg-white shadow-[0_4px_30px_0_rgba(0,0,0,0.15)] duration-200 animate-in slide-in-from-right-4"
						: "flex h-[776px] max-h-[calc(100vh-172px)] w-[416px] flex-col overflow-hidden rounded-lg bg-white shadow-[0_4px_30px_0_rgba(0,0,0,0.15)] duration-200 animate-in slide-in-from-bottom-4"
				}
				role="dialog"
				aria-label="Ask PerformYard Chat"
				aria-modal={isExpanded || undefined}
			>
				<ChatContent />
			</div>

			{/* Goal Edit Panel — fullscreen only */}
			{isExpanded && isGoalEditPanelOpen && (
				<div className="relative h-full w-[640px] border-l border-solid border-neutral-2 bg-white duration-200 animate-in slide-in-from-right-4">
					<GoalEditPanel
						onClose={closeGoalEditPanel}
						onCreateGoal={handleCreateGoal}
						onCreateAllGoals={handleCreateAllGoals}
						onDeleteGoal={markGoalDeleted}
						creatingSuggestionIds={creatingSuggestionIds}
						isCreatingAll={isCreatingAll}
					/>
				</div>
			)}

			{/* Competency Edit Panel — fullscreen only */}
			{isExpanded && isCompetencyEditPanelOpen && (
				<div className="relative h-full w-[640px] border-l border-solid border-neutral-2 bg-white duration-200 animate-in slide-in-from-right-4">
					<CompetencyEditPanel onClose={closeCompetencyEditPanel} />
				</div>
			)}

			{/* Arrow pointer — floating only */}
			{!isExpanded && (
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="27"
					height="16"
					viewBox="0 0 27 16"
					fill="none"
					className="absolute -bottom-3 right-[13px]"
					style={{ width: "26.667px", height: "16px" }}
				>
					<path
						d="M11.4502 15.2191C12.4914 16.2603 14.1824 16.2603 15.2237 15.2191L25.8862 4.55655C26.6526 3.79019 26.8775 2.64897 26.461 1.64936C26.0445 0.649746 25.0782 0 23.9953 0L2.67026 0.00833059C1.59568 0.00833059 0.621063 0.658077 0.204559 1.65769C-0.211945 2.6573 0.0212975 3.79852 0.779335 4.56488L11.4418 15.2274L11.4502 15.2191Z"
						fill="white"
					/>
				</svg>
			)}
		</div>,
		document.body
	);
};

export default ChatContainer;
